import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Button, Dialog, DialogTitle, DialogContent, DialogActions,
    IconButton, TextField, FormControl, InputLabel, Select, MenuItem, CircularProgress, Tooltip, useTheme, useMediaQuery
} from '@mui/material';
import { toastSuccess, alertValidacao, confirmExclusao } from '../services/alerts';

import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';

import api from '../services/api';
import { isVisualizador } from '../services/auth';

const TIPO_ANEXO_OPTIONS = [
    { value: 'LAUDO_EXTERNO', label: 'Laudo Externo' },
    { value: 'EXAME', label: 'Exame' },
    { value: 'DOCUMENTO_JUDICIAL', label: 'Documento Judicial' },
    { value: 'ENCAMINHAMENTO_RECEBIDO', label: 'Encaminhamento Recebido' },
    { value: 'CONTRATO_ASSINADO', label: 'Contrato Assinado' },
    { value: 'TERMO_ASSINADO', label: 'Termo Assinado' },
    { value: 'RECEITUARIO_EXTERNO', label: 'Receituário Externo' },
    { value: 'OUTRO', label: 'Outro' },
];

export default function AnexosPaciente() {
    const { id } = useParams();
    const navigate = useNavigate();
    const idPaciente = id;
    const soLeitura = isVisualizador();

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));

    const [anexos, setAnexos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openDialog, setOpenDialog] = useState(false);
    const [uploading, setUploading] = useState(false);

    // Estados para o Modal de Visualização
    const [viewModalOpen, setViewModalOpen] = useState(false);
    const [fileToView, setFileToView] = useState(null);
    const [viewerBlobUrl, setViewerBlobUrl] = useState(null);
    const [viewerLoading, setViewerLoading] = useState(false);

    const [formData, setFormData] = useState({
        descricao: '',
        tipoAnexo: 'OUTRO',
        file: null
    });

    useEffect(() => {
        if (idPaciente) {
            carregarAnexos();
        }
    }, [idPaciente]);

    const carregarAnexos = async () => {
        setLoading(true);
        try {
            const response = await api.get(`/anexos/paciente/${idPaciente}`);
            const d = response.data;
            setAnexos(Array.isArray(d) ? d : (d?.content ?? []));
        } catch (error) {
            console.error('Erro ao carregar anexos:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleOpenDialog = () => {
        setFormData({ descricao: '', tipoAnexo: 'OUTRO', file: null });
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
    };

    const handleFileChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            setFormData({ ...formData, file: e.target.files[0] });
        }
    };

    const handleSave = async () => {
        if (!formData.file || !formData.descricao) {
            alertValidacao('Preencha a descrição e selecione um arquivo.');
            return;
        }

        setUploading(true);
        try {
            const data = new FormData();
            data.append('file', formData.file);
            data.append('descricao', formData.descricao);
            data.append('tipoAnexo', formData.tipoAnexo);
            data.append('idPaciente', idPaciente);

            await api.post('/anexos', data);

            toastSuccess('Arquivo anexado com sucesso!');
            carregarAnexos();
            handleCloseDialog();
        } catch (error) {
            console.error('Erro no upload:', error);
        } finally {
            setUploading(false);
        }
    };

    const handleDelete = async (id) => {
        const ok = await confirmExclusao({ text: 'Tem certeza que deseja excluir este anexo?' });
        if (!ok) return;
        try {
            await api.delete(`/anexos/${id}`);
            toastSuccess('Anexo excluído com sucesso!');
            carregarAnexos();
        } catch (error) {
            console.error('Erro ao excluir anexo:', error);
        }
    };

    const formatBytes = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const dm = 2;
        const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
    };

    const formatTipo = (tipo) => {
        const option = TIPO_ANEXO_OPTIONS.find(opt => opt.value === tipo);
        return option ? option.label : tipo;
    };

    const isImage = (fileName, format) => {
        if (format && format.startsWith('image/')) return true;
        return /\.(jpg|jpeg|png|gif|webp)$/i.test(fileName);
    };

    const isPDF = (fileName, format) => {
        if (format === 'application/pdf') return true;
        return /\.pdf$/i.test(fileName);
    };

    const fetchBlobUrl = async (anexo) => {
        const res = await api.get(anexo.urlDownload, { responseType: 'blob' });
        const contentType = res.headers['content-type'] || anexo.formatoArquivo || 'application/octet-stream';
        return URL.createObjectURL(new Blob([res.data], { type: contentType }));
    };

    const handleDownload = async (anexo) => {
        try {
            const url = await fetchBlobUrl(anexo);
            const a = document.createElement('a');
            a.href = url;
            a.download = anexo.nomeOriginal;
            document.body.appendChild(a);
            a.click();
            a.remove();
            setTimeout(() => URL.revokeObjectURL(url), 60000);
        } catch (err) {
            console.error('Erro ao baixar anexo:', err);
        }
    };

    // Nova Lógica Inteligente para Visualização
    const handleActionView = async (anexo) => {
        if (isPDF(anexo.nomeOriginal, anexo.formatoArquivo) && isMobile) {
            try {
                const url = await fetchBlobUrl(anexo);
                window.open(url, '_blank');
                setTimeout(() => URL.revokeObjectURL(url), 60000);
            } catch (err) {
                console.error('Erro ao abrir anexo:', err);
            }
            return;
        }
        setFileToView(anexo);
        setViewModalOpen(true);
        setViewerLoading(true);
        try {
            const url = await fetchBlobUrl(anexo);
            setViewerBlobUrl(url);
        } catch (err) {
            console.error('Erro ao carregar visualização:', err);
        } finally {
            setViewerLoading(false);
        }
    };

    const handleCloseViewModal = () => {
        setViewModalOpen(false);
        setFileToView(null);
        if (viewerBlobUrl) {
            URL.revokeObjectURL(viewerBlobUrl);
            setViewerBlobUrl(null);
        }
    };

    return (
        <Box sx={{ flexGrow: 1, px: isMobile ? 1 : 0 }}>

            {/* Cabeçalho da Página */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
                <IconButton onClick={() => navigate('/pacientes')} sx={{ bgcolor: '#fff', boxShadow: '0 2px 5px rgba(0,0,0,0.05)' }}>
                    <ArrowBackIcon />
                </IconButton>
                <Typography variant={isMobile ? 'h5' : 'h4'} sx={{ fontWeight: 'bold', color: '#333' }}>
                    Gestão de Anexos
                </Typography>
            </Box>

            {/* Container Principal */}
            <Paper elevation={0} sx={{ p: { xs: 2, sm: 4 }, bgcolor: '#fff', borderRadius: 2, boxShadow: '0 2px 10px rgba(0,0,0,0.05)' }}>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="h6" sx={{ color: '#1ABB9C', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 4, height: 24, bgcolor: '#1ABB9C', borderRadius: 1 }} />
                        Arquivos do Paciente
                    </Typography>
                    {!soLeitura && (
                        <Button variant="outlined" startIcon={<AddIcon />} onClick={handleOpenDialog} size="small" sx={{ borderColor: '#1ABB9C', color: '#1ABB9C' }}>
                            Anexar Arquivo
                        </Button>
                    )}
                </Box>

                <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
                    <TableContainer>
                        <Table size={isMobile ? "small" : "medium"}>
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                                    {/* Esconde a Descrição no celular (fica só o nome do arquivo) */}
                                    <TableCell sx={{ display: isMobile ? 'none' : 'table-cell' }}><strong>Descrição</strong></TableCell>

                                    <TableCell><strong>Tipo</strong></TableCell>
                                    <TableCell><strong>Arquivo</strong></TableCell>

                                    {/* Esconde a Data no celular e tablet */}
                                    <TableCell sx={{ display: isTablet || isMobile ? 'none' : 'table-cell' }}><strong>Data</strong></TableCell>

                                    <TableCell align="right"><strong>Ações</strong></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading ? (
                                    <TableRow>
                                        <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                                            <CircularProgress size={24} sx={{ color: '#1ABB9C' }} />
                                        </TableCell>
                                    </TableRow>
                                ) : anexos.length > 0 ? (
                                    anexos.map((item) => (
                                        <TableRow key={item.id_anexo} hover>
                                            <TableCell sx={{ display: isMobile ? 'none' : 'table-cell' }}>
                                                {item.descricao}
                                            </TableCell>

                                            <TableCell>{formatTipo(item.tipoAnexo)}</TableCell>

                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <InsertDriveFileIcon color="action" fontSize="small" />
                                                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                                        <Typography variant="body2" noWrap sx={{ maxWidth: isMobile ? 100 : 150 }} title={item.nomeOriginal}>
                                                            {item.nomeOriginal}
                                                        </Typography>
                                                        {/* Mostra a descrição curtinha embaixo do nome do arquivo SÓ no celular, já que escondemos a coluna */}
                                                        {isMobile && (
                                                            <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 100 }}>
                                                                {item.descricao}
                                                            </Typography>
                                                        )}
                                                        <Typography variant="caption" color="text.secondary">
                                                            ({formatBytes(item.tamanhoBytes)})
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </TableCell>

                                            <TableCell sx={{ display: isTablet || isMobile ? 'none' : 'table-cell' }}>
                                                {item.dataUpload}
                                            </TableCell>

                                            <TableCell align="right">
                                                {/* Agora usamos nossa nova função inteligente para abrir PDFs */}
                                                <Tooltip title="Visualizar">
                                                    <IconButton size="small" color="info" onClick={() => handleActionView(item)}>
                                                        <VisibilityIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>

                                                <Tooltip title="Baixar">
                                                    <IconButton size="small" color="primary" onClick={() => handleDownload(item)}>
                                                        <DownloadIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>

                                                {!soLeitura && (
                                                    <Tooltip title="Excluir">
                                                        <IconButton size="small" color="error" onClick={() => handleDelete(item.id_anexo)}>
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={5} align="center" sx={{ py: 4, color: '#999' }}>
                                            Nenhum arquivo anexado a este paciente.
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            </Paper>

            {/* Modal de Upload */}
            <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
                {/* ... (Mesmo código do Modal de Upload) ... */}
                <DialogTitle sx={{ fontWeight: 'bold' }}>Anexar Arquivo</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
                        <TextField
                            fullWidth
                            label="Descrição Curta"
                            required
                            value={formData.descricao}
                            onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                        />

                        <FormControl fullWidth required>
                            <InputLabel>Tipo de Anexo</InputLabel>
                            <Select
                                value={formData.tipoAnexo}
                                label="Tipo de Anexo"
                                onChange={(e) => setFormData({ ...formData, tipoAnexo: e.target.value })}
                            >
                                {TIPO_ANEXO_OPTIONS.map((opt) => (
                                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <Box sx={{ border: '1px dashed #ccc', p: 3, borderRadius: 2, textAlign: 'center' }}>
                            <input
                                accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx"
                                style={{ display: 'none' }}
                                id="raised-button-file"
                                type="file"
                                onChange={handleFileChange}
                            />
                            <label htmlFor="raised-button-file">
                                <Button variant="outlined" component="span" sx={{ borderColor: '#1ABB9C', color: '#1ABB9C' }}>
                                    Escolher Arquivo
                                </Button>
                            </label>
                            {formData.file && (
                                <Typography variant="body2" sx={{ mt: 2, color: 'success.main' }}>
                                    Arquivo selecionado: {formData.file.name}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={handleCloseDialog} disabled={uploading}>Cancelar</Button>
                    <Button
                        onClick={handleSave}
                        variant="contained"
                        disabled={uploading}
                        startIcon={uploading ? <CircularProgress size={20} color="inherit" /> : null}
                        sx={{ bgcolor: '#1ABB9C', '&:hover': { bgcolor: '#129A80' } }}
                    >
                        {uploading ? 'Enviando...' : 'Fazer Upload'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Modal de Visualização (Imagens ou PDFs no Desktop) */}
            <Dialog
                open={viewModalOpen}
                onClose={handleCloseViewModal}
                maxWidth="lg"
                fullWidth
                fullScreen={isMobile}
            >
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: '#333', color: 'white', p: 2 }}>
                    <Typography variant="h6" noWrap sx={{ maxWidth: '80%' }}>
                        {fileToView?.nomeOriginal}
                    </Typography>
                    <IconButton onClick={handleCloseViewModal} size="small" sx={{ color: 'white' }}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent dividers sx={{ p: 0, height: '75vh', display: 'flex', justifyContent: 'center', alignItems: 'center', bgcolor: '#e0e0e0' }}>
                    {viewerLoading && <CircularProgress sx={{ color: '#1ABB9C' }} />}

                    {!viewerLoading && fileToView && viewerBlobUrl && isImage(fileToView.nomeOriginal, fileToView.formatoArquivo) && (
                        <img
                            src={viewerBlobUrl}
                            alt={fileToView.nomeOriginal}
                            style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                        />
                    )}

                    {!viewerLoading && fileToView && viewerBlobUrl && isPDF(fileToView.nomeOriginal, fileToView.formatoArquivo) && (
                        <iframe
                            src={viewerBlobUrl}
                            title={fileToView.nomeOriginal}
                            width="100%"
                            height="100%"
                            style={{ border: 'none' }}
                        />
                    )}

                    {!viewerLoading && fileToView && !isImage(fileToView.nomeOriginal, fileToView.formatoArquivo) && !isPDF(fileToView.nomeOriginal, fileToView.formatoArquivo) && (
                        <Box sx={{ textAlign: 'center', p: 4 }}>
                            <InsertDriveFileIcon sx={{ fontSize: 80, color: '#9e9e9e', mb: 2 }} />
                            <Typography variant="h6" color="textSecondary" sx={{ mb: 1 }}>
                                Visualização não disponível
                            </Typography>
                            <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                                O formato deste arquivo não pode ser visualizado diretamente no navegador.
                            </Typography>
                            <Button
                                variant="contained"
                                onClick={() => handleDownload(fileToView)}
                                startIcon={<DownloadIcon />}
                                sx={{ bgcolor: '#1ABB9C', '&:hover': { bgcolor: '#129A80' } }}
                            >
                                Baixar Arquivo
                            </Button>
                        </Box>
                    )}
                </DialogContent>
            </Dialog>

        </Box>
    );
}